/**
 * haptics.ts
 *
 * Thin wrapper around @capacitor/haptics.
 * - Native (iOS/Android via Capacitor): real tactile feedback
 * - Web: silent no-op — never throws
 *
 * Usage:
 *   import { haptic } from '@/lib/haptics';
 *   await haptic('submit');   // medium impact on form submit
 *   await haptic('success');  // notification success
 *   await haptic('error');    // notification error
 *   await haptic('light');    // light tap (e.g. toggle)
 */

type HapticPreset = 'submit' | 'success' | 'error' | 'light' | 'heavy' | 'selection';

export async function haptic(preset: HapticPreset = 'light'): Promise<void> {
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');

    switch (preset) {
      case 'submit':
        await Haptics.impact({ style: ImpactStyle.Medium });
        break;
      case 'success':
        await Haptics.notification({ type: NotificationType.Success });
        break;
      case 'error':
        await Haptics.notification({ type: NotificationType.Error });
        break;
      case 'light':
        await Haptics.impact({ style: ImpactStyle.Light });
        break;
      case 'heavy':
        await Haptics.impact({ style: ImpactStyle.Heavy });
        break;
      case 'selection':
        await Haptics.selectionStart();
        await Haptics.selectionChanged();
        await Haptics.selectionEnd();
        break;
    }
  } catch {
    // Web build or plugin unavailable — silent no-op
  }
}
